import { defineStore } from "pinia";
import { ref, watch } from "vue";

import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";
import { useGlobalLoadingScreen } from "@/stores/globalLoadingScreenStore";
import { useClearStorageSession } from "@/composable/useClearStorageSession";
import { createBroadcastChannel } from "@polargold/pg-frontend-core";

export const LOGOUT_CHANNEL_NAME = "autoq-logout";

export const useAuthenticationStore = defineStore("authenticationStore", () => {
    const accessToken = ref<string | undefined>();
    const tokenExpiresIn = ref<Date>(new Date());
    const tokenIsValid = ref<boolean>(false);

    const {
        write,
        read,
        remove
    } = new StorageHelper(StorageName.AccessToken, false);

    const globalLoading = useGlobalLoadingScreen();
    const { clear: clearStorageSession } = useClearStorageSession();

    const logoutChannel = createBroadcastChannel(LOGOUT_CHANNEL_NAME);

    const saveAccessTokenToLocalStorage = (token: string) => {
        write(token);
    };

    const retrieveAccessTokenFromLocalStorage = () => {
        const token = read<string>();

        if (!token) return;

        saveAccessTokenToStore(token);
    };

    const saveAccessTokenToStore = (token: string) => {
        accessToken.value = token;
    };

    const saveAccessToken = (token: string) => {
        saveAccessTokenToStore(token);
        saveAccessTokenToLocalStorage(token);
    };

    const removeAccessTokenFromStore = () => {
        accessToken.value = undefined;
    };

    const removeAccessTokenFromLocalStorage = () => {
        remove();
    };

    const removeAccessToken = () => {
        removeAccessTokenFromLocalStorage();
        removeAccessTokenFromStore();
    };

    const setExpireDate = (date: Date) => {
        tokenExpiresIn.value = date;
    };

    const setTokenValidity = (isValid: boolean) => {
        tokenIsValid.value = isValid;
    };

    const redirectToLogin = () => {
        globalLoading.activateLoadingScreen(
            () => window.open(import.meta.env.VITE_UNIFIED_LOGIN_URL, "_self")
        );
    };

    const applyLogoutLocally = async () => {
        redirectToLogin();
        removeAccessToken();
        await clearStorageSession();
    };

    const logout = async () => {
        await applyLogoutLocally();
        logoutChannel?.postMessage(true);
    };

    // Another tab already applied the logout locally; mirror it here too without
    // re-broadcasting, otherwise every open tab would echo the message.
    logoutChannel?.addEventListener("message", applyLogoutLocally);

    const dispose = () => {
        logoutChannel?.removeEventListener("message", applyLogoutLocally);
        logoutChannel?.close();
    };

    watch(tokenIsValid, async (value) => {
        if (value) return;

        await logout();
    });

    return {
        accessToken,
        saveAccessToken,
        removeAccessToken,
        retrieveAccessTokenFromLocalStorage,
        tokenExpiresIn,
        setExpireDate,
        tokenIsValid,
        setTokenValidity,
        logout,
        dispose
    };
});
